import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import Swal from 'sweetalert2';
import './ForgotPassword.css';
import { API_URL } from '../global';
import { useNavigate } from 'react-router-dom';

const LOGO = "https://res.cloudinary.com/dwrwkl5b3/image/upload/v1772788624/ChatGPT_Image_Mar_6__2026__02_43_41_PM-removebg-preview_wyghgu.png";

/* ── Inline SVG Icons ── */
const IconEmail = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
    fill="none" stroke="#ff6a00" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <polyline points="2,4 12,13 22,4"/>
  </svg>
);

const IconSend = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
    fill="none" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/>
    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);

const IconBack = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
    fill="none" stroke="#0047cc" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const IconShield = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24"
    fill="none" stroke="url(#shieldGrad)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <defs>
      <linearGradient id="shieldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0047cc"/>
        <stop offset="100%" stopColor="#ff6a00"/>
      </linearGradient>
    </defs>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    <polyline points="9 12 11 14 15 10"/>
  </svg>
);

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [headerH, setHeaderH] = useState(75);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const measure = () => {
      const nav = document.querySelector('nav') || document.querySelector('header');
      if (nav) setHeaderH(nav.offsetHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const formik = useFormik({
    initialValues: { email: '' },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
    }),
    onSubmit: async (values) => {
      Swal.fire({
        title: 'Sending Reset Link...',
        text: 'Please wait while we process your request.',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
      try {
        const response = await axios.post(`${API_URL}/forgotpassword`, {
          email: values.email,
        });
        if (response.status === 200) {
          Swal.fire({
            icon: 'success',
            title: 'Link Sent!',
            text: response.data.message,
            timer: 2500,
            showConfirmButton: false,
          }).then(() => setSent(true));
        }
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: error.response?.data?.message || 'An error occurred. Please try again later.',
        });
      }
    },
  });

  return (
    <div
      className="zuppa_lms_forgot_pg_root"
      style={{ marginTop: `${headerH}px`, minHeight: `calc(100vh - ${headerH}px)` }}
    >
      {/* ── Animated Background Orbs ── */}
      <span className="zuppa_lms_forgot_pg_orb zuppa_lms_forgot_pg_orb_1" />
      <span className="zuppa_lms_forgot_pg_orb zuppa_lms_forgot_pg_orb_2" />
      <span className="zuppa_lms_forgot_pg_orb zuppa_lms_forgot_pg_orb_3" />

      {/* ── Card ── */}
      <div className="zuppa_lms_forgot_pg_card">

        {/* Top accent bar */}
        <div className="zuppa_lms_forgot_pg_accent_bar" />

        {/* Logo */}
        <img src={LOGO} alt="Zuppa Logo" className="zuppa_lms_forgot_pg_logo" />

        {/* Shield Icon */}
        <div className="zuppa_lms_forgot_pg_shield_wrap">
          <IconShield />
        </div>

        {!sent ? (
          <>
            <h2 className="zuppa_lms_forgot_pg_title">Forgot Password?</h2>
            <p className="zuppa_lms_forgot_pg_sub">
              Enter your registered email address and we'll send you a password reset link.
            </p>

            {/* Form */}
            <form
              className="zuppa_lms_forgot_pg_form"
              onSubmit={formik.handleSubmit}
              noValidate
            >
              <div className="zuppa_lms_forgot_pg_field">
                <label className="zuppa_lms_forgot_pg_label">Email Address</label>
                <div className="zuppa_lms_forgot_pg_input_wrap">
                  <span className="zuppa_lms_forgot_pg_input_icon">
                    <IconEmail />
                  </span>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className={`zuppa_lms_forgot_pg_input${
                      formik.touched.email && formik.errors.email
                        ? ' zuppa_lms_forgot_pg_input_bad' : ''
                    }`}
                    {...formik.getFieldProps('email')}
                  />
                </div>
                {formik.touched.email && formik.errors.email && (
                  <p className="zuppa_lms_forgot_pg_err">{formik.errors.email}</p>
                )}
              </div>

              <button type="submit" className="zuppa_lms_forgot_pg_btn">
                <IconSend /> Send Reset Link
              </button>
            </form>
          </>
        ) : (
          /* Success State */
          <div className="zuppa_lms_forgot_pg_success">
            <div className="zuppa_lms_forgot_pg_success_icon">✓</div>
            <h2 className="zuppa_lms_forgot_pg_title">Check Your Email!</h2>
            <p className="zuppa_lms_forgot_pg_sub">
              A password reset link has been sent to <strong>{formik.values.email}</strong>. Please check your inbox.
            </p>
          </div>
        )}

        {/* Back to Login */}
        <button
          className="zuppa_lms_forgot_pg_back_btn"
          onClick={() => navigate('/lms/login')}
        >
          <IconBack /> Back to Login
        </button>

      </div>
    </div>
  );
};

export default ForgotPassword;